"use client"

import { Fragment, useEffect, useState } from "react"
import { Popover, Transition } from "@headlessui/react"
import { signout, getCustomer } from "@lib/data/customer"
import Link from "next/link"
import User from "@modules/common/icons/user"
import {
  Settings,
  LogOutIcon,
  UserCircle,
  ShoppingCart,
  Building2,
  Trophy,
  Heart,
} from "lucide-react"
import Divider from "@modules/common/components/divider"
import { useTranslation } from "react-i18next"

const UserProfileDropdown = () => {
  const { t } = useTranslation()
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false)
  const [customer, setCustomer] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)

  const open = () => setCartDropdownOpen(true)
  const close = () => setCartDropdownOpen(false)
  const handleLogout = async () => {
    if (isLoading) return
    setIsLoading(true)
    await signout()
    setIsLoading(false)
  }

  const openAndCancel = () => {
    open()
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const c = await getCustomer()
        setCustomer(c)
      } catch (e) {
        // hata yönetimi
      }
    }
    fetchData()
  }, [])

  const isAllowedToSeeReferrals = customer?.customer_type?.title === "Streamer" || customer?.customer_type?.title === "Seller"

  return (
    <div
      className="z-50 h-full"
      onMouseEnter={openAndCancel}
      onMouseLeave={close}
    >
      <Popover className="flex relative flex-col justify-center pt-1 h-full border-none ring-0">
        <div className="flex h-11 w-11 shrink-0 cursor-pointer flex-row items-center justify-center rounded-full border border-orange-200 bg-white p-0 text-orange-600 shadow-sm transition-colors hover:bg-orange-50">
          <User />
        </div>
        <Transition
          show={cartDropdownOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel
            static
            className="hidden small:block absolute right-0 top-[calc(100%+8px)] w-64 rounded-2xl border border-slate-200 bg-slate-50 p-2 text-slate-700 shadow-xl"
            data-testid="nav-cart-dropdown"
          >
            {customer && (
              <div className="mb-1 flex flex-col items-start rounded-xl border border-slate-200 bg-white p-3">
                <span className="text-sm font-semibold text-slate-800">
                  {customer.first_name} {customer.last_name}
                </span>
              </div>
            )}
            <div className="m-1 w-full rounded-lg p-2 transition-colors hover:bg-white">
              <Link
                className="flex flex-row w-full"
                href={"/account/dashboard"}
              >
                <Settings className="mr-2 w-4 h-4" />
                <span>{t("navbar.overview")}</span>
              </Link>
            </div>
            <div className="m-1 w-full rounded-lg p-2 transition-colors hover:bg-white">
              <Link
                className="flex flex-row w-full"
                href={"/account/dashboard/profile"}
              >
                <UserCircle className="mr-2 w-4 h-4" />
                <span>{t("navbar.profile")}</span>
              </Link>
            </div>
            {/* Bank details and wallet links are temporarily disabled. */}
            <div className="m-1 w-full rounded-lg p-2 transition-colors hover:bg-white">
              <Link
                className="flex flex-row w-full"
                href={"/account/dashboard/addresses"}
              >
                <Building2 className="mr-2 w-4 h-4" />
                <span>{t("navbar.address")}</span>
              </Link>
            </div>
            <div className="m-1 w-full rounded-lg p-2 transition-colors hover:bg-white">
              <Link
                className="flex flex-row w-full"
                href={"/account/dashboard/orders"}
              >
                <ShoppingCart className="mr-2 w-4 h-4" />
                <span>{t("navbar.orders")}</span>
              </Link>
            </div>
            <div className="m-1 w-full rounded-lg p-2 transition-colors hover:bg-white">
              <Link
                className="flex flex-row w-full"
                href={"/account/dashboard/favorites"}
              >
                <Heart className="mr-2 w-4 h-4" />
                <span>{t("product.favorites")}</span>
              </Link>
            </div>
            {isAllowedToSeeReferrals && (
              <div className="m-1 w-full rounded-lg p-2 transition-colors hover:bg-white">
                <Link
                  className="flex flex-row w-full"
                  href={"/account/dashboard/referrals"}
                >
                  <Trophy className="mr-2 w-4 h-4" />
                  <span>{t("navbar.referrals")}</span>
                </Link>
              </div>
            )}

            <Divider className="mx-2 bg-slate-200" />
            <div
              className="m-1 flex w-full cursor-pointer flex-row rounded-lg p-2 text-red-600 transition-colors hover:bg-white"
              onClick={handleLogout}
            >
              <LogOutIcon className="mr-2 w-4 h-4" />
              <span>{isLoading ? t("navbar.loading") : t("navbar.logout")}</span>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  )
}

export default UserProfileDropdown
